import React, { useState, useEffect, useContext } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import { StateContext } from "../../store/context";
import { TitleArea } from "../../components/Typography/titleArea";
import { LoadingData, NoDataDetails } from "./components/noDetails";
import ReactMarkdown from "react-markdown";
import { Modal, Button, message } from "antd";
import { CopyOutlined, CheckOutlined, CodeOutlined } from "@ant-design/icons";

import GPT from "../../../src/static/icons/gpt.jpg";
import {
  getStockChatResponse,
  getAIStreamingResponse,
  getStockAssistantStreamingRes,
} from "../../services/ai.service";
import { socket } from "../../config/socketService";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { usePreventCopy } from "../../hooks/usePreventCopy";
import { StockSelectOptions } from "./components/stockSelectOptions";
import { flattenMetrics } from "../../helper";
import { markdownComponents } from "../../components/markdownComponents";
import DisclaimerButton from "../../components/DisclaimerButton";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./stockAssistant.theme.css";

const StockAssistant: React.FC = (): React.ReactElement => {
  usePreventCopy();
  const {
    baseCurrency,
    authUser: { allowed, sub_plan, role_name },
  } = useContext(StateContext);

  const [primaryStock, setPrimaryStock] = useState<any>();
  const [stocks, setStocks] = useState<any>({});
  const [adverts, setAds] = useState<any>();
  const [aiResponse, setAiResponse] = useState("");
  const [reqLimitReached, setReqLimitReached] = useState(false);
  const [renewLimitTime, setRenewLimitTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState("");
  const [hasReceivedFirstChunk, setHasReceivedFirstChunk] = useState(false);
  const [userMessage, setUserMessage] = useState("");
  const [metricsData, setMetricsData] = useState<any>(null);
  const [isRawModalOpen, setIsRawModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  const {
    response: metricsAndEquations,
    loading: metricsAndEquationsLoading,
    execute: getMetricsAndEquations,
  } = useFetchApi("/api/metrics-and-equations", {}, "GET");

  useEffect(() => {
    getStocks();
    getMetricsAndEquations();
  }, []);

  useEffect(() => {
    if (response && Object.keys(response).length) {
      setStocks(response);
    }
  }, [response]);

  useEffect(() => {
    if (metricsAndEquations && !metricsAndEquationsLoading) {
      setMetricsData(metricsAndEquations);
    }
  }, [metricsAndEquations, metricsAndEquationsLoading]);

  const handleGenerate = (
    primaryStock: any,
    stocksToCompare: any,
    peEstimate: string,
    expectedGrowth: string,
  ) => {
    setPrimaryStock(primaryStock);
  };

  useEffect(() => {
    if (primaryStock && Object.keys(primaryStock).length) {
      setTimeout(() => {
        handleGenerate(primaryStock, [], "", "");
      }, 100);
    }
  }, [baseCurrency]);

  useEffect(() => {
    socket.on("stock-assistant-chunk", (chunk) => {
      if (!hasReceivedFirstChunk) {
        setHasReceivedFirstChunk(true);
        setLoading(false);
      }
      setStreamingResponse((prev) => prev + chunk);
    });

    socket.on("stock-assistant-complete", (finalResponse) => {
      setAiResponse(finalResponse);
      setLoading(false);
      setHasReceivedFirstChunk(false);
    });

    socket.on("stock-assistant-error", (error) => {
      console.error("Analysis streaming error:", error);
      setAiResponse("An error occurred during analysis. Please try again.");
      setLoading(false);
      setHasReceivedFirstChunk(false);
    });

    return () => {
      socket.off("stock-assistant-chunk");
      socket.off("stock-assistant-complete");
      socket.off("stock-assistant-error");
    };
  }, [hasReceivedFirstChunk]);

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const handleAnalyze = async () => {
    try {
      setLoading(true);
      setStreamingResponse("");
      setHasReceivedFirstChunk(false);

      const flattenedMetrics = flattenMetrics(metricsData);

      const payload = {
        stock_ids: [...(primaryStock?.id ? [primaryStock.id] : [])],
        message: userMessage,
        selectedStocks: [...(primaryStock?.id ? [primaryStock] : [])],
        selectedMetrics: flattenedMetrics,
        filter: "quarterly",
        period: "5",
        baseCurrency: primaryStock.short_name === "CSE" ? "LKR" : "QAR",
      };

      const res = await getStockChatResponse(payload);
      setAiResponse(res);

      if (res && res.status === "AI_LIMIT_EXCEEDED" && res.nextReset) {
        setReqLimitReached(true);
        setRenewLimitTime(res.nextReset);
        setLoading(false);
      } else {
        try {
          await getStockAssistantStreamingRes(res);
        } catch (error) {
          console.error("Error during streaming analysis:", error);
          setAiResponse("An error occurred during analysis. Please try again.");
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCopyRaw = async () => {
    try {
      await navigator.clipboard.writeText(streamingResponse);
      setCopied(true);
      message.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error("Failed to copy. Please try manually.");
    }
  };

  const isPremiumUser =
    sub_plan === "Premium Plan" && role_name === "Premium Customer";
  const isStaff =
    role_name !== "Premium Customer" || role_name !== "Basic Customer";

  return (
    <div className="vi-sta vi-glass-sheen flex md:flex-row flex-col items-center md:items-start w-full select-none">
      <div
        className={`flex flex-col w-full ${
          isPremiumUser ? "md:w-full" : "md:!w-9/12"
        } h-full items-start px-4 py-3`}
      >
        <div className="w-full flex items-start justify-between gap-4">
          <TitleArea
            variant="page"
            title="Stock Assistant"
            subTitle="Ask anything about your selected stock — AI-powered insights, analysis, and guidance."
          />
          <DisclaimerButton />
        </div>

        <hr className="hr-line w-full my-2" />

        <div className="w-full">
          <div className="w-[50%] md:w-[500px] mb-4">
            <h2 className="text-[16px] font-bold font-inter mb-2">
              Select Stock
            </h2>
            <StockSelectOptions
              selectedStock={primaryStock}
              setSelectedStock={setPrimaryStock}
            />
          </div>
          <hr className="hr-line w-full my-2" />

          <div className="w-full mb-4">
            <label
              htmlFor="userMessage"
              className="block text-sm font-bold text-gray-700 mb-2"
            >
              Your Message
            </label>
            <textarea
              id="userMessage"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              placeholder="Ask a question about the stock"
              className="vi-sta-control w-full px-4 py-3 rounded-lg focus:outline-none resize-none font-inter text-sm"
              rows={3}
              disabled={loading}
            />
          </div>

          <div className="w-full flex flex-col items-end">
            {reqLimitReached && (
              <p className="text-red-600 text-sm font-bold mb-1">
                You have reached your limit for recommendations. Access will
                reset on {formatDateTime(renewLimitTime)}
              </p>
            )}
            <button
              onClick={handleAnalyze}
              className="mt-[14px] bg-[#0075E3] !rounded-[24px] disabled:cursor-not-allowed disabled:opacity-50 !w-[116px] h-8 text-white font-inter"
              disabled={
                loading ||
                hasReceivedFirstChunk ||
                !primaryStock ||
                !userMessage.trim() ||
                reqLimitReached
              }
            >
              {loading ? "Analyzing..." : "Get Analysis"}
            </button>
          </div>

          {/* Analysis Header with View Summary button */}
          <div className="flex items-center justify-between mt-2">
            <TitleArea title="Analysis" />
          </div>

          {loading ? (
            <LoadingData />
          ) : !streamingResponse && !loading ? (
            <NoDataDetails />
          ) : (
            <div className="chat-container">
              <img src={GPT} className="chat-gpt-icon" alt="GPT Icon" />
              <div className="prose prose-sm !text-wrap lg:max-w-none text-[#667085] text-left font-inter overflow-x-hidden">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {streamingResponse}
                </ReactMarkdown>
              </div>
            </div>
          )}
          {isStaff && streamingResponse && !loading && (
            <button
              onClick={() => setIsRawModalOpen(true)}
              className="vi-sta-summary-btn flex items-center gap-1.5 px-3 mt-4 h-8 text-sm text-[#0075E3] border border-[#0075E3] rounded-[24px] hover:bg-blue-50 transition-colors font-inter"
            >
              <CodeOutlined style={{ fontSize: 14 }} />
              View Summary
            </button>
          )}
        </div>
      </div>

      {/* Raw Response Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <CodeOutlined style={{ color: "var(--vi-accent)" }} />
            <span className="font-inter font-semibold text-[15px]">
              AI Summary
            </span>
          </div>
        }
        open={isRawModalOpen}
        onCancel={() => setIsRawModalOpen(false)}
        width={780}
        wrapClassName="vi-sta-modal"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              icon={copied ? <CheckOutlined /> : <CopyOutlined />}
              type="primary"
              onClick={handleCopyRaw}
              style={{
                backgroundColor: "var(--vi-accent)",
                color: "var(--vi-accent-on)",
                borderRadius: 24,
                fontFamily: "Inter, sans-serif",
              }}
            >
              {copied ? "Copied!" : "Copy"}
            </Button>
            <Button
              onClick={() => setIsRawModalOpen(false)}
              style={{ borderRadius: 24, fontFamily: "Inter, sans-serif" }}
            >
              Close
            </Button>
          </div>
        }
      >
        <div
          className="bg-[#F8F9FA] border border-[#D0D5DD] rounded-lg p-4 mt-1 overflow-auto font-mono text-sm text-[#344054] leading-relaxed"
          style={{
            maxHeight: "60vh",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {streamingResponse}
        </div>
      </Modal>
    </div>
  );
};

export default StockAssistant;
